import { defineStore } from 'pinia'
import * as repairCommandApi from '../services/api/repair-command'

export interface IRepairCommandData {
  note: string
  approvals: Array<any>
  car: any
  id: number
  status: string
  request: string
  condition: string
  scheduled_at: string
  car_id: number
  creator_id: number
  leader_id: number
  manager_id: number
  repair_card_id: number
  customer_id: number
  assignee_ids: Array<number>
  repair_card: IRepairCardData
  customer: any
  _assignees: string
}

export interface IRepairCardData {
  id: number
  date_in: string
  date_out: string
  accessaries: Array<any>
  conducted_items: Array<any>
  note: string
}

export const useRepairCommandStore = defineStore('repairCommand', {
  state: () => ({
    repairCommand: {
      note: '',
      approvals: [],
      car: {},
      id: 0,
      status: '',
      request: '',
      condition: '',
      scheduled_at: '',
      car_id: 0,
      creator_id: 0,
      leader_id: 0,
      manager_id: 0,
      repair_card_id: 0,
      customer_id: 0,
      assignee_ids: [],
      repair_card: {
        id: 0,
        date_in: '',
        date_out: '',
        accessaries: [],
        conducted_items: [],
        note: '',
      },
      customer: {},
      _assignees: '',
    } as IRepairCommandData,
    isLoading: false,
  }),

  actions: {
    setRepairCommand(data: IRepairCommandData) {
      this.repairCommand = data
    },

    setStatus(status: string) {
      this.repairCommand.status = status
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async getRepairCommand(repair_command_id: number) {
      try {
        this.setLoading(true)
        const { data } = await repairCommandApi.get(repair_command_id)
        this.setRepairCommand(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async updateRepairCommand(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await repairCommandApi.update(this.repairCommand.id, payload)
        this.setStatus(data.status)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async rejectRepairCommand(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await repairCommandApi.reject(this.repairCommand.id, payload)
        this.setRepairCommand(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async approveRepairCommand() {
      try {
        this.setLoading(true)
        const { data } = await repairCommandApi.approve(this.repairCommand.id, {})
        this.setRepairCommand(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async applyRepairCommand(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await repairCommandApi.apply(this.repairCommand.id, payload)
        this.setRepairCommand(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async remakeRepairCommand(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await repairCommandApi.remake(this.repairCommand.id, payload)
        this.setRepairCommand(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async processRepairCommand(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await repairCommandApi.process(this.repairCommand.id, payload)
        this.setRepairCommand(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async createRepairCommand(payload: any) {
      try {
        this.setLoading(true)
        const { data } = await repairCommandApi.create(payload)
        this.setLoading(false)
        return data
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})

export const useRepairCommandListStore = defineStore('repairCommandList', {
  state: () => ({
    count: 0,
    repairCommands: [] as IRepairCommandData[],
    repairCommandsByCar: [] as IRepairCommandData[],
    total: 0,
    isLoading: false,
  }),

  actions: {
    setCount(data: number) {
      this.count = data
    },

    setRepairCommands(data: IRepairCommandData[]) {
      this.repairCommands = data
    },

    setRepairCommandsByCar(data: IRepairCommandData[]) {
      this.repairCommandsByCar = data
    },

    setTotal(value: number) {
      this.total = value
    },

    setLoading(value: boolean) {
      this.isLoading = value
    },

    async countRepairCommands() {
      try {
        this.setLoading(true)
        const { data } = await repairCommandApi.count()
        this.setCount(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listRepairCommands(params: any) {
      try {
        this.setLoading(true)
        const result = await repairCommandApi.list(params)
        const { data, total } = result as any
        this.setRepairCommands(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listAllRepairCommands(params: any) {
      try {
        this.setLoading(true)
        const result = await repairCommandApi.listAll(params)
        const { data, total } = result as any
        this.setRepairCommands(data)
        this.setTotal(total)
        this.setLoading(false)
        return result
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },

    async listRepairCommandsByCar(car_id: number) {
      try {
        this.setLoading(true)
        const result = await repairCommandApi.listAll({ car_id })
        const { data } = result as any
        this.setRepairCommandsByCar(data)
        this.setLoading(false)
      } catch (e) {
        this.setLoading(false)
        throw new Error(String(e))
      }
    },
  },
})
